import { useEffect, useState } from 'react';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { useToast } from '@oxpa/shared/hooks/use-toast';
import { createClient } from '@oxpa/shared/lib/supabase/client';
import { useRouter } from 'next/navigation';

export function useNativePush() {
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const { toast } = useToast();
  const supabase = createClient();
  const router = useRouter();

  useEffect(() => {
    // Выполняем подписку только если на нативной платформе
    if (!Capacitor.isNativePlatform()) return;

    let isRegistered = false;

    const registerPush = async () => {
      try {
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          // console.warn('Push permissions denied.');
          return;
        }

        await PushNotifications.register();
      } catch (e) {
        // console.error('Failed to register push', e);
      }
    };

    const addListeners = async () => {
      await PushNotifications.addListener('registration', async (token: Token) => {
        // console.log('Push registration success, token: ' + token.value);
        setFcmToken(token.value);
        
        // Сохраняем токен в профиль пользователя в фоновом режиме
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          try {
            // Предполагаем, что у нас есть поле push_tokens (или аналогичное хранилище)
            await supabase
              .from('employees')
              .update({ fcm_token: token.value } as any)
              .eq('id', session.user.id);
          } catch (e) {
            // console.error('Failed to save push token to DB', e);
          }
        }
      });

      await PushNotifications.addListener('registrationError', (error: any) => {
        // console.error('Error on registration: ' + JSON.stringify(error));
      });

      await PushNotifications.addListener(
        'pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          // Показываем IN-APP уведомление (toast), если приложение открыто
          toast({
            title: notification.title || 'Новое уведомление',
            description: notification.body || '',
          });
        }
      );

      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          // console.log('Push action performed: ' + JSON.stringify(notification));
          const data = notification.notification.data;
          if (data && data.path) {
            router.push(data.path);
          }
        }
      );
      
      isRegistered = true;
    };

    if (!isRegistered) {
      addListeners();
      registerPush();
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllListeners();
      }
    };
  }, [supabase, toast, router]);

  return { fcmToken, isNative: Capacitor.isNativePlatform() };
}
